import * as P2PUtils from "@classwize/p2p-utils";
import * as Api from "../api/api";
import * as liveViewApi from "../api/liveViewApi";
import _ from "lodash";
import { retryAsync } from "./retryAsync";
import * as windowUtils from "../utils/windowUtils";
import md5 from "md5";
import { datadogLogs } from "@datadog/browser-logs";
import { useP2PContextStore } from "stores/p2pContextStore";
import shallow from "zustand/shallow";
import { useUserSessionStore } from "stores/v2/userSessionStore";

const retry3Async = _.curry(retryAsync)(3, 500);
const tokenEndBufferSeconds = 60;

const ABLY_LOG_LEVEL_DEFAULT = 1;
const ABLY_LOG_LEVEL_DEBUG = 4;
const ablyLogLevel = localStorage.getItem("LIVE_VIEW_ABLY_DEBUG") === "true" ? ABLY_LOG_LEVEL_DEBUG : ABLY_LOG_LEVEL_DEFAULT;

export const initFinishedEnum = {
	completed: 0,
	failed: 1,
	skipped: 2,
	cancelled: 3,
};

export const getChannelId = (deviceId, classroomid) => {
	const deviceChannelId = deviceId.replace(/[^a-z0-9]/gi, "-");
	const classroomChannelId = md5(classroomid);
	return `${deviceChannelId}/${classroomChannelId}`;
};

class P2PInitFailedError extends Error {
	constructor(message, cause, initStatus) {
		super(message);
		this.cause = cause;
		this.name = "P2PInitFailedError";
		this.initStatus = initStatus;
	}
}
/**
 * P2PManager
 *
 * Singleton. Manages P2P Live View for one classroom at a time.
 *
 * Lifecycle:
 *
 * init = async (classroomId, classroomEndUnix, zoomConfig)
 *
 *		sets up xirsys signaler
 *		sets up close timeout at the scheduled end of the given class
 *		called from the ClassroomWindowsV2 component on mount, changes in the UserSessionStore and
 *			completed loading of the classroom
 *
 * close = ()
 *
 * 		closes xirsys signaler
 * 		clears close timeout
 *
 * This singleton instance is exported as p2pManager
 *
 */
class P2PManager {
	constructor() {
		this.classroomId = null;
		this.classroomHash = null;
		this.classroomEndUnix = null;
		this.p2pExpiryUnix = null;

		this.signaler = null;
		this.signalerActiveUsername = null;
		this.signalerReconnectAttempts = 0;

		this.ice = null;
		this.xirsysInfoFetched = null;
		this.channel = null;

		this.initializingSignaler = true;
		this.signalerTokens = {};

		this.teacherSignalTokenPromiseResolve = null;
		this.teacherSignalTokenPromise = new Promise((resolve, reject) => {
			this.teacherSignalTokenPromiseResolve = resolve;
		});

		this.studentsWithInfo = new Set(); // students who have already been sent p2p info (should be a set)
		this.queuedStudentInfo = new Set();
		this.noSignalTokenTeacherCounter = 0; // Used for retrying teacher token till rate is limited
		this.noSignalTokenStudentCounter = 0; // Used to make sure we only call signal token fetching limited number of times
		this.delayTokenFetching = 10000; // Set this value to number of seconds to delay fetching signalling token the second time
		this.initializing = false;
		this.initializedPromise = null;
		this.initializedPromiseResolve = null;
		this.initializedPromiseReject = null;

		this.abortController = null;
		this.closeTimeout = null;
		this.debugTimingData = {};
	}

	/**
	 * This function filters out the students that have screenshot data
	 * Once filtered they are mapped in studentId to get all the devices associated with a single student
	 * Comparison is made between the existing tokens and missing ones.
	 * Only the missing tokens are fetched (if any)
	 * @param {Pass the list of all students in a classroom} students
	 */
	checkUsersToFetchToken = async (students) => {
		const filteredUsersWithScreenshotData = students.filter((user) => {
			const { screenshots } = user;
			return screenshots.length > 0;
		});
		filteredUsersWithScreenshotData.forEach((student) => {
			student.screenshots.forEach((screenshot) => {
				const platform = windowUtils.getPlatformType(screenshot);
				const chrome = platform === "Chromebook";
				const connectionPeerId = chrome ? "" : `_${p2pManager.signalerActiveUsername}`;
				const uniqueUsername = `${student.username}_peerAgentId_${screenshot.chrome_id}${connectionPeerId}`;
				if (!this.studentsWithInfo.has(uniqueUsername)) {
					this.studentsWithInfo.add(`${student.username}_peerAgentId_${screenshot.chrome_id}${connectionPeerId}`);
				}
			});
		});
		const studentIds = Array.from(this.studentsWithInfo);
		const studentIdsDiff = _.difference(studentIds, _.keys(this.signalerTokens));
		if (studentIdsDiff.length > 0 && !this.initializing) {
			switch (this.noSignalTokenStudentCounter) {
				case 0:
					this.getSignallingTokensForAllStudents(studentIdsDiff);
					break;
				default:
					if (this.noSignalTokenStudentCounter < 3) {
						this.fetchSignalTokenTimeout = setTimeout(this.getSignallingTokensForAllStudents(studentIdsDiff), this.delayTokenFetching);
					} else {
						clearTimeout(this.fetchSignalTokenTimeout);
					}
			}
		}
	};

	/**
	 * This function is used to fetch all the tokens for the student objects passed
	 * @param {Pass the list opf all the members/students in the current class} students
	 */
	getSignallingTokensForAllStudents = async (students) => {
		this.noSignalTokenStudentCounter += 1;
		let tokens;
		try {
			tokens = await retry3Async(() =>
				liveViewApi.getSignallingTokens(
					{
						classroom: this.classroomHash,
						users: students,
						expiry_unix: this.p2pExpiryUnix,
					},
					{ abortController: this.abortController }
				)
			);
		} catch (err) {
			if (err.name === "AbortError") {
				console.warn("Live View - Aborted fetching signalling tokens for students, P2PManager re-init may have been called");
			} else {
				console.error("Live View - Failed fetching signalling tokens for students:", err);
			}
			throw err;
		}
		_.assign(this.signalerTokens, tokens);
	};

	init = async (classroomId, classroomEndUnix, zoomConfig) => {
		/* Queue any further init calls that happen when initialization is already in progress - not doing this can cause strange behaviour such as multiple signalers being initiatied
		 * Only the latest init call is queued */
		if (this.initializing) {
			if (!this._isUpdatedInitParams(classroomId, classroomEndUnix)) {
				return initFinishedEnum.skipped;
			}
			this._cancelInit();
		}

		// need to close before initializing everything, otherwise we wipe our state
		if (this.signaler) {
			this.close();
		}
		this.signalerReconnectAttempts = 0;

		this.initializing = true;
		this.initializedPromise = new Promise((resolve, reject) => {
			this.initializedPromiseResolve = resolve;
			this.initializedPromiseReject = reject;
		});
		this.abortController = new AbortController();
		clearTimeout(this.closeTimeout);
		this.closeTimeout = setTimeout(
			async () => {
				await this.deleteXirsysChannel();
				this.close();
			},
			classroomEndUnix * 1000 - Date.now()
		);

		if (!this._isUpdatedInitParams(classroomId, classroomEndUnix)) {
			this.initializing = false;
			this.initializedPromiseResolve(initFinishedEnum.skipped);
			return this.initializedPromise;
		}

		this.classroomId = classroomId;
		this.classroomEndUnix = classroomEndUnix;
		this.p2pExpiryUnix = Math.ceil(classroomEndUnix + tokenEndBufferSeconds);
		this.signalerTokens = {};
		this.studentsWithInfo.clear();

		const { active_device, active_username } = useUserSessionStore.getState();
		this.signalerActiveUsername = active_username + Math.random().toString(36).substring(7);
		const channel = getChannelId(active_device, classroomId);
		this.classroomHash = md5(classroomId);

		// Although we don't use the xirsys signaler anymore, we need to create the channel anyway for the STUN/TURN server to work
		try {
			await retry3Async(() => liveViewApi.createChannel({ channel: channel }, { signal: this.abortController.signal }));
		} catch (err) {
			// requests are aborted if there is an updated init call while init is already in progress - updated init is run instead
			if (err.name === "AbortError") {
				return initFinishedEnum.cancelled;
			} else {
				console.error(`Live View - Failed to create xirsys channel ${channel}. ${err}`);
				const _err = new P2PInitFailedError(err.message, err, initFinishedEnum.failed);
				this._initFailed(_err);
				throw _err;
			}
		}

		this.channel = channel;

		const icePromise = retry3Async(
			() =>
				liveViewApi.getIce({
					channel: channel,
					expiry_unix: this.p2pExpiryUnix,
					preferred_gateway: localStorage.getItem("XIRSYS_PREFERRED_GATEWAY"),
				}),
			{ abortController: this.abortController }
		);
		const signalTokensPromise = retry3Async(
			() =>
				liveViewApi.getSignallingTokens({
					classroom: this.classroomHash,
					users: [this.signalerActiveUsername],
					expiry_unix: this.p2pExpiryUnix,
				}),
			{ abortController: this.abortController }
		);

		let signalTokens;
		try {
			signalTokens = await signalTokensPromise;
			this.teacherSignalTokenPromiseResolve();
		} catch (err) {
			// requests are aborted if there is an updated init call while init is already in progress - updated init is run instead
			if (err.name === "AbortError") {
				return initFinishedEnum.cancelled;
			} else {
				console.error("Live View - Failed fetching the teachers signalling token");
				const _err = new P2PInitFailedError(err.message, err, initFinishedEnum.failed);
				this._initFailed(_err);
				throw _err;
			}
		}
		_.assign(this.signalerTokens, signalTokens);

		let ice;
		try {
			ice = await icePromise;
		} catch (err) {
			// requests are aborted if there is an updated init call while init is already in progress - updated init is run instead
			if (err.name === "AbortError") {
				return this.initializedPromise;
			} else {
				console.error("Live View - Failed fetching xirsys ice data");
				const _err = new P2PInitFailedError(err.message, err, initFinishedEnum.failed);
				this._initFailed(_err);
				throw _err;
			}
		}
		this.ice = ice;
		this.xirsysInfoFetched = true;
		this.initializing = false;
		this.initializedPromiseResolve(initFinishedEnum.completed);

		this.sendP2PInfo([...this.queuedStudentInfo]);
		this.queuedStudentInfo.clear();
		this._updateEvent();
		return this.initializedPromise;
	};

	// options.force -> array of users to forcefully send P2PInfo - they are not skipped when they have previously been sent info
	sendP2PInfo = async (students) => {
		// if we're still waiting on Xirsys fetches, queue the send for these students
		if (!students.length) {
			return;
		}
		if (!this.xirsysInfoFetched) {
			console.log("queueing p2p info");
			students.forEach((x) => this.queuedStudentInfo.add(x));
			return;
		}

		// only fetch signaller tokens for students we don't already have tokens for
		const studentIds = students.map((s) => `${s.id}_peerAgentId_${s.peerAgentId}${s.connectionPeerId}`);
		const studentIdsDiff = _.difference(studentIds, _.keys(this.signalerTokens));

		let tokens;
		if (studentIdsDiff.length) {
			try {
				tokens = await retry3Async(() =>
					liveViewApi.getSignallingTokens(
						{
							classroom: this.classroomHash,
							users: studentIdsDiff,
							expiry_unix: this.p2pExpiryUnix,
						},
						{ abortController: this.abortController }
					)
				);
			} catch (err) {
				if (err.name === "AbortError") {
					console.error("Live View - Aborted fetching signalling tokens for students, likely due to P2PManager re-init");
				} else {
					console.error("Live View - Failed fetching signalling tokens for students:", err);
				}
				throw err;
			}
			_.assign(this.signalerTokens, tokens);
		}

		students.forEach(async (student) => {
			const uniqueUsername = `${student["id"]}_peerAgentId_${student["peerAgentId"]}${student["connectionPeerId"]}`;
			try {
				this.debugTimingData[uniqueUsername] = new Date().getTime();

				await retry3Async(() =>
					Api.post("/event/p2p-init", {
						recipients: [student["id"]],
						event_object: {
							peerAgentId: student["peerAgentId"],
							channel: this.channel,
							signalHost: "x", // TODO: Remove in contract, no longer needed. event-service will 400 if it's missing atm
							signalToken: this.signalerTokens[uniqueUsername],
							signalExpiryUnix: this.p2pExpiryUnix,
							remotePeerId: this.signalerActiveUsername,
							ice: this.ice,
						},
					})
				);
			} catch (err) {
				console.error(
					`Live View - Failing sending p2p info to ${student["id"]} with peerAgentId ${student["peerAgentId"]}, event-service message send failed`,
					err
				);
				throw err;
			}
		});
	};

	deleteXirsysChannel = async () => {
		// Clear the Xirsis channel when class ends
		const { active_device } = useUserSessionStore.getState();
		const channel = getChannelId(active_device, this.classroomId);
		try {
			await retry3Async(() => liveViewApi.deleteChannel({ channel: channel }));
		} catch (err) {
			//continue on error
			console.error(`Live View - Failed to delete xirsys channel ${channel}. ${err}`);
		}
	};

	close = () => {
		if (this.signaler) {
			this.signaler.client.close();
		}
		this.signalerReady = false;
		clearTimeout(this.closeTimeout);
		this._reset();
		this._updateEvent();
	};

	initSignaler = async () => {
		await this.teacherSignalTokenPromise;
		await this._initSignaler(this.signalerTokens[this.signalerActiveUsername], this.channel);
	};

	_initSignaler = async (token, channel) => {
		if (this.signaler) {
			return;
		}

		this.signaler = new P2PUtils.Signaler(token, ablyLogLevel);
		this.signaler.client.connect();
		const signalerChannel = this.signaler.channel(channel);

		signalerChannel.on("attached", () => {
			console.debug("Live View - Ably channel attached");
		});
		signalerChannel.on("detached", () => {
			console.debug("Live View - Ably channel detached");
		});
		signalerChannel.on("suspended", () => {
			console.debug("Error: Live View - Ably channel suspended. See https://ably.com/docs/channels#states");
			datadogLogs.logger.error("Live View - Ably channel suspended. See https://ably.com/docs/channels#states");
		});
		signalerChannel.on("failed", () => {
			console.debug("Live View - Ably channel failed. See https://ably.com/docs/channels#states");
			datadogLogs.logger.error("Live View - Ably channel failed. See https://ably.com/docs/channels#states");
		});

		this.signaler.client.connection.on("connected", () => {
			console.info("Live View - Ably connected");
			datadogLogs.logger.info("Live View - Ably connected");
			signalerChannel.presence.enter();
			this.signalerReconnectAttempts = 0;
			this.signalerReady = true;
			this._updateEvent();
		});
		this.signaler.client.connection.on("disconnected", () => {
			console.warn("Live View - Ably client disconnected. See https://ably.com/docs/connect/states");
			datadogLogs.logger.warn("Live View - Ably client disconnected. See https://ably.com/docs/connect/states");
		});
		this.signaler.client.connection.on("suspended", () => {
			console.debug("Error: Live View - Ably client suspended. See https://ably.com/docs/connect/states");
			datadogLogs.logger.error("Live View - Ably client suspended. See https://ably.com/docs/connect/states");
		});
		this.signaler.client.connection.on("failed", () => {
			console.debug(
				`Error: Live View - Ably failed. See https://ably.com/docs/connect/states. Attempting to reconnect, attempt ${this.signalerReconnectAttempts}`
			);
			datadogLogs.logger.error("Live View - Ably client failed. See https://ably.com/docs/connect/states");

			this.signalerReconnectAttempts++;
			this.signalerReady = false;
			this.signaler.client.close();
			this.signaler = null;
			this._updateEvent();

			// Limit the retries as it's possible to get into an infinite retry loop if classwize is left idle and the auth tokens expire.
			// Avoiding adding too much complexity by handling this casew. It will fix it self next time a class is started or another class is switched to
			if (this.signalerReconnectAttempts < 5) {
				this._initSignaler(token, channel);
			}
		});

		// DEBUG
		this.signaler.client.connection.on("initialized", () => console.debug("Live View - Ably initialized"));
		this.signaler.client.connection.on("connecting", () => console.debug("Live View - Ably connecting"));
		this.signaler.client.connection.on("closing", () => console.debug("Live View - Ably closing"));
		this.signaler.client.connection.on("closed", () => console.debug("Live View - Ably closed"));
		// END DEBUG

		// DEBUG
		signalerChannel.on("initialized", () => {
			console.debug("Live View - Ably initialized");
		});
		signalerChannel.on("attaching", () => {
			console.debug("Live View - Ably channel attaching");
		});
		signalerChannel.on("detaching", () => {
			console.debug("Live View - Ably channel detaching");
		});
		// END DEBUG
	};

	closeSignaler = () => {
		if (this.signaler) {
			this.signaler.client.close();
		}
		this.signaler = null;
		this.signalerReady = false;
		this._updateEvent();
	};

	_handlePeerConnected = (message) => {
		console.log(message);
	};

	_initFailed = (err) => {
		this.initializing = false;
		this.close();
	};

	_reset = () => {
		this.classroomId = null;
		this.classroomEndUnix = null;
		this.p2pExpiryUnix = null;
		this.channel = null;

		this.ice = null;
		this.xirsysInfoFetched = null;

		this.initializingSignaler = false;
		this.signalerReady = false;
		this.signaler = null;
		this.signalerArgs = null;
		this.signalerInitAttempts = 0;

		this.teacherSignalTokenPromiseResolve = null;
		this.teacherSignalTokenPromise = new Promise((resolve, reject) => {
			this.teacherSignalTokenPromiseResolve = resolve;
		});

		this.onReadyChange = null;
		this.onInitFailed = null;
		this.closeTimeout = null;

		this.initializing = false;
		this.initializedPromise = null;
		this.initializedPromiseResolve = null;
	};

	_isUpdatedInitParams(classroomId, classroomEndUnix) {
		return classroomId !== this.classroomId || classroomEndUnix > this.classroomEndUnix;
	}

	_cancelInit() {
		this.close();
		this.abortController.abort(); // cancels all in-flight fetch requests in init()
	}

	addContextUpdateHandler = (func) => {
		this.contextUpdateEventListener = func;
	};

	_updateEvent = () => {
		useP2PContextStore.setState(
			{
				ready: this.signalerReady && this.xirsysInfoFetched,
				signaler: this.signaler,
				ice: this.ice,
				channelName: this.channel,
			},
			shallow
		);
	};
}

const p2pManager = new P2PManager();
export default p2pManager;
